import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { motion } from 'framer-motion';
import { AnimatedCounter, SkeletonStatCard } from '../ui';
import AnalyticsChart from './AnalyticsChart';
import { useWebsite } from '../../contexts/WebsiteContext';
import { Empty, Eyebrow, HairlineGrid, PageHeader, Panel, PanelBody, Pill, Stat, Tag } from '../ui/kit';
import { MARKS } from '../../lib/marks';

interface MetricItem {
  name: string;
  visitors: number;
  percent: number;
}

interface AnalyticsData {
  overview: {
    visitors: number;
    pageViews: number;
    bounceRate: number;
    averageSessionDuration: number;
  };
  visitors: {
    today: number;
    yesterday: number;
    thisWeek: number;
    thisMonth: number;
    change: string;
  };
  topPages: Array<{ path: string; views: number; title: string }>;
  sources: Array<{ name: string; visitors: number; percent: number }>;
  devices: MetricItem[];
  browsers: MetricItem[];
  countries: MetricItem[];
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}m ${secs}s`;
}

/** Get a favicon URL for a traffic source domain */
function getSourceFaviconUrl(sourceName: string): string | null {
  if (!sourceName || sourceName === 'Direct' || sourceName === '(none)') return null;
  // sourceName from Umami is typically just a domain like "google.com"
  const domain = sourceName.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=16`;
}

/** Check if a source is a known social/search platform */
function isKnownPlatform(name: string): string | null {
  const lower = name.toLowerCase();
  if (lower.includes('google')) return 'google';
  if (lower.includes('facebook') || lower.includes('fb.com')) return 'facebook';
  if (lower.includes('twitter') || lower.includes('x.com') || lower === 't.co') return 'twitter';
  if (lower.includes('instagram')) return 'instagram';
  if (lower.includes('yelp')) return 'yelp';
  if (lower.includes('bing')) return 'bing';
  if (lower.includes('yahoo')) return 'yahoo';
  return null;
}

type DateRange = '7d' | '30d' | '90d';

const AnalyticsPage: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange>('30d');
  const { websites, isLoading: websitesLoading, selectedWebsiteId, isAllSelected } = useWebsite();

  const liveWebsites = useMemo(
    () => websites.filter(w => w.status === 'active' && w.contentSetupComplete),
    [websites]
  );

  // Analytics requires a specific site — fall back to first live site when "All" is selected
  const selectedWebsite = isAllSelected
    ? (liveWebsites[0]?.id || '')
    : selectedWebsiteId;

  const currentWebsite = liveWebsites.find(w => w.id === selectedWebsite) || liveWebsites[0];

  const { data: analyticsData, isLoading: analyticsLoading, isError: analyticsError } = useQuery({
    queryKey: ['analytics', selectedWebsite, dateRange],
    queryFn: async () => {
      const response = await axios.get(`/api/websites/${selectedWebsite}/analytics?period=${dateRange}`);
      return response.data?.data?.websiteStats as AnalyticsData | undefined;
    },
    enabled: !!selectedWebsite,
    refetchInterval: 60000,
    retry: false,
  });

  // Timeseries data for chart
  const { data: timeseriesData } = useQuery({
    queryKey: ['analytics-timeseries', selectedWebsite, dateRange],
    queryFn: async () => {
      const response = await axios.get(`/api/websites/${selectedWebsite}/analytics/pageviews?period=${dateRange}`);
      return response.data?.data as Array<{ date: string; visitors: number; pageviews: number }>;
    },
    enabled: !!selectedWebsite,
    staleTime: 60000,
  });

  const isLoading = websitesLoading || (analyticsLoading && !!selectedWebsite);

  if (liveWebsites.length === 0 && !websitesLoading) {
    return (
      <div className="space-y-6">
        <PageHeader
          eyebrow="Traffic"
          title="Analytics"
          description="Traffic overview and visitor insights"
        />

        <Empty
          mark={MARKS.analytics}
          eyebrow="Traffic"
          title="No analytics yet"
          description="Visitor counts, sources and the pages people actually read show up once your site is live."
          hint="Tracking starts the moment the site is deployed — no tag to install."
          action={<Pill to="/dashboard" tone="outline">Back to Dashboard</Pill>}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Traffic"
        title="Analytics"
        description="Traffic overview and visitor insights"
        actions={
          [
            { value: '7d', label: '7 days' },
            { value: '30d', label: '30 days' },
            { value: '90d', label: '90 days' },
          ].map((range) => (
            <motion.button
              key={range.value}
              onClick={() => setDateRange(range.value as DateRange)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`inline-flex items-center justify-center rounded-full px-3.5 py-1.5 text-[13px] font-medium transition-all ${
                dateRange === range.value
                  ? 'bg-primary text-white'
                  : 'bg-white/60 text-primary ring-1 ring-zinc-300 hover:bg-white hover:ring-zinc-400'
              }`}
            >
              {range.label}
            </motion.button>
          ))
        }
      />

      {/* Website info */}
      {currentWebsite && liveWebsites.length > 1 && (
        <Panel className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Eyebrow>Viewing</Eyebrow>
              <span className="text-[15px] font-medium text-primary">{currentWebsite.businessName || currentWebsite.domain}</span>
            </div>
            <span className="font-mono text-[13px] text-muted">
                {currentWebsite.domain}
            </span>
          </div>
        </Panel>
      )}

      {/* Error State */}
      {analyticsError && !isLoading && (
        <Empty
          title="Analytics unavailable"
          description="Could not load analytics data. This may happen if tracking is not yet configured for this website."
        />
      )}

      {/* Overview Cards */}
      <HairlineGrid cols="grid-cols-2 lg:grid-cols-4">
        {isLoading ? (
          <>
            {[...Array(4)].map((_, i) => (
              <SkeletonStatCard key={i} />
            ))}
          </>
        ) : analyticsData?.overview ? (
          <>
            <StatCard
              label="Total Visitors"
              value={analyticsData.overview.visitors}
              change={analyticsData.visitors?.change}
            />
            <StatCard
              label="Page Views"
              value={analyticsData.overview.pageViews}
            />
            <StatCard
              label="Avg. Session"
              valueString={formatDuration(analyticsData.overview.averageSessionDuration)}
            />
            <StatCard
              label="Bounce Rate"
              valueString={`${analyticsData.overview.bounceRate}%`}
            />
          </>
        ) : null}
      </HairlineGrid>

      {/* Traffic Chart */}
      {timeseriesData && timeseriesData.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.3 }}
          className="rounded-xl border border-zinc-200 bg-white p-6"
        >
          <Eyebrow className="mb-4">Traffic Trend</Eyebrow>
          <AnalyticsChart data={timeseriesData} period={dateRange} />
        </motion.div>
      )}

      {/* Visitors Summary */}
      {analyticsData?.visitors && (
        <Panel>
          <PanelBody>
            <Eyebrow className="mb-4">Visitors</Eyebrow>
            <HairlineGrid cols="grid-cols-2 md:grid-cols-4">
              <Stat label="Today" value={<AnimatedCounter value={analyticsData.visitors.today} />} />
              <Stat label="Yesterday" value={<AnimatedCounter value={analyticsData.visitors.yesterday} />} />
              <Stat label="This Week" value={<AnimatedCounter value={analyticsData.visitors.thisWeek} />} />
              <Stat label="This Month" value={<AnimatedCounter value={analyticsData.visitors.thisMonth} />} />
            </HairlineGrid>
          </PanelBody>
        </Panel>
      )}

      {/* Top Pages & Traffic Sources */}
      {analyticsData?.topPages && analyticsData?.sources && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Top Pages */}
          <Panel className="p-6">
            <Eyebrow className="mb-4">Top Pages</Eyebrow>
            {analyticsData.topPages.length > 0 ? (
              <div className="space-y-3">
                {analyticsData.topPages.map((page, index) => (
                  <motion.div
                    key={page.path}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.2 }}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-zinc-100 rounded-full flex items-center justify-center text-xs font-medium text-muted">
                        {index + 1}
                      </span>
                      <div>
                        <p className="text-[15px] font-medium text-primary">{page.title}</p>
                        <p className="font-mono text-[13px] text-muted">{page.path}</p>
                      </div>
                    </div>
                    <span className="font-mono text-[13px] font-medium text-primary">{page.views.toLocaleString()}</span>
                  </motion.div>
                ))}
              </div>
            ) : (
              <p className="text-[15px] text-muted">No page data yet</p>
            )}
          </Panel>

          {/* Traffic Sources */}
          <Panel className="p-6">
            <Eyebrow className="mb-4">Traffic Sources</Eyebrow>
            {analyticsData.sources.length > 0 ? (
              <div className="space-y-4">
                {analyticsData.sources.map((source, index) => (
                  <motion.div
                    key={source.name}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.2 }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <SourceIcon name={source.name} />
                        <span className="text-sm font-medium text-primary">{source.name}</span>
                      </div>
                      <span className="font-mono text-[13px] text-muted">{source.visitors} ({source.percent}%)</span>
                    </div>
                    <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${source.percent}%` }}
                        transition={{ duration: 0.8, delay: 0.3 + index * 0.1, ease: 'easeOut' }}
                        className="h-full rounded-full bg-primary"
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <p className="text-[15px] text-muted">No traffic data yet</p>
            )}
          </Panel>
        </div>
      )}

      {/* Devices & Browsers */}
      {analyticsData && (analyticsData.devices?.length > 0 || analyticsData.browsers?.length > 0) && (
        <div className="grid md:grid-cols-2 gap-6">
          {/* Devices */}
          {analyticsData.devices?.length > 0 && (
            <Panel className="p-6">
              <Eyebrow className="mb-4">Devices</Eyebrow>
              <div className="space-y-4">
                {analyticsData.devices.map((device, index) => (
                  <motion.div
                    key={device.name}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.2 }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <DeviceIcon type={device.name} />
                        <span className="text-sm font-medium text-primary capitalize">{device.name.toLowerCase()}</span>
                      </div>
                      <span className="font-mono text-[13px] text-muted">{device.visitors} ({device.percent}%)</span>
                    </div>
                    <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${device.percent}%` }}
                        transition={{ duration: 0.8, delay: 0.3 + index * 0.1, ease: 'easeOut' }}
                        className="h-full rounded-full bg-primary"
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </Panel>
          )}

          {/* Browsers */}
          {analyticsData.browsers?.length > 0 && (
            <Panel className="p-6">
              <Eyebrow className="mb-4">Browsers</Eyebrow>
              <div className="space-y-4">
                {analyticsData.browsers.map((browser, index) => (
                  <motion.div
                    key={browser.name}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05, duration: 0.2 }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <BrowserIcon name={browser.name} />
                        <span className="text-sm font-medium text-primary">{browser.name}</span>
                      </div>
                      <span className="font-mono text-[13px] text-muted">{browser.visitors} ({browser.percent}%)</span>
                    </div>
                    <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${browser.percent}%` }}
                        transition={{ duration: 0.8, delay: 0.3 + index * 0.1, ease: 'easeOut' }}
                        className="h-full rounded-full bg-primary"
                      />
                    </div>
                  </motion.div>
                ))}
              </div>
            </Panel>
          )}
        </div>
      )}

      {/* Countries */}
      {(analyticsData?.countries?.length ?? 0) > 0 && (
        <Panel className="p-6">
          <Eyebrow className="mb-4">Countries</Eyebrow>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {analyticsData!.countries.map((country, index) => (
              <motion.div
                key={country.name}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05, duration: 0.2 }}
              >
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <GlobeIcon className="w-4 h-4 text-zinc-400" />
                    <span className="text-sm font-medium text-primary">{country.name}</span>
                  </div>
                  <span className="font-mono text-[13px] text-muted">{country.visitors} ({country.percent}%)</span>
                </div>
                <div className="h-2 bg-zinc-100 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${country.percent}%` }}
                    transition={{ duration: 0.8, delay: 0.3 + index * 0.1, ease: 'easeOut' }}
                    className="h-full rounded-full bg-primary"
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
};

// Stat Card Component
interface StatCardProps {
  label: string;
  value?: number;
  valueString?: string;
  change?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, valueString, change }) => (
  <div className="bg-white p-5">
    <div className="mb-2 flex items-center justify-between">
      <Eyebrow>{label}</Eyebrow>
      {change && (
        <Tag tone={change.startsWith('+') ? 'live' : change.startsWith('-') ? 'danger' : 'neutral'}>
          {change}
        </Tag>
      )}
    </div>
    <p className="font-display text-[1.75rem] font-bold leading-none tracking-[-0.03em] text-primary">
      {value !== undefined ? <AnimatedCounter value={value} /> : valueString}
    </p>
  </div>
);

// --- Source Icon with favicon / platform icon ---
const SourceIcon: React.FC<{ name: string }> = ({ name }) => {
  const platform = isKnownPlatform(name);
  const faviconUrl = getSourceFaviconUrl(name);

  if (name === 'Direct' || name === '(none)') {
    return <GlobeIcon className="w-4 h-4 text-zinc-400" />;
  }

  if (platform === 'google') {
    return <GoogleIcon className="w-4 h-4" />;
  }
  if (platform === 'facebook') {
    return <FacebookIcon className="w-4 h-4" />;
  }
  if (platform === 'twitter') {
    return <TwitterIcon className="w-4 h-4" />;
  }
  if (platform === 'instagram') {
    return <InstagramIcon className="w-4 h-4" />;
  }

  if (faviconUrl) {
    return (
      <img
        src={faviconUrl}
        alt=""
        className="w-4 h-4 rounded-sm"
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
        }}
      />
    );
  }

  return <GlobeIcon className="w-4 h-4 text-zinc-400" />;
};

// --- Device Icons ---
const DeviceIcon: React.FC<{ type: string }> = ({ type }) => {
  const lower = type.toLowerCase();
  if (lower === 'desktop' || lower === 'laptop') return <MonitorIcon className="w-4 h-4 text-zinc-400" />;
  if (lower === 'mobile') return <SmartphoneIcon className="w-4 h-4 text-zinc-400" />;
  if (lower === 'tablet') return <TabletIcon className="w-4 h-4 text-zinc-400" />;
  return <MonitorIcon className="w-4 h-4 text-zinc-400" />;
};

// --- Browser Icons ---
const BrowserIcon: React.FC<{ name: string }> = ({ name }) => {
  const lower = name.toLowerCase();
  if (lower.includes('chrome')) return <ChromeIcon className="w-4 h-4" />;
  if (lower.includes('safari')) return <SafariIcon className="w-4 h-4" />;
  if (lower.includes('firefox')) return <FirefoxIcon className="w-4 h-4" />;
  if (lower.includes('edge')) return <EdgeIcon className="w-4 h-4" />;
  if (lower.includes('opera')) return <OperaIcon className="w-4 h-4" />;
  return <GlobeIcon className="w-4 h-4 text-zinc-400" />;
};

// =====================
// SVG Icons
// =====================

const UsersIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
  </svg>
);

const EyeIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);

const ClockIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

const TrendingIcon: React.FC<{ className?: string }> = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941" />
  </svg>
);

const GlobeIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5a17.92 17.92 0 0 1-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418" />
  </svg>
);

// Device icons
const MonitorIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 0 1-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0 1 15 18.257V17.25m6-12V15a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 15V5.25A2.25 2.25 0 0 1 5.25 3h13.5A2.25 2.25 0 0 1 21 5.25Z" />
  </svg>
);

const SmartphoneIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 1.5H8.25A2.25 2.25 0 0 0 6 3.75v16.5a2.25 2.25 0 0 0 2.25 2.25h7.5A2.25 2.25 0 0 0 18 20.25V3.75a2.25 2.25 0 0 0-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3" />
  </svg>
);

const TabletIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5h3m-6.75 2.25h10.5a2.25 2.25 0 0 0 2.25-2.25V4.5a2.25 2.25 0 0 0-2.25-2.25H6.75A2.25 2.25 0 0 0 4.5 4.5v15a2.25 2.25 0 0 0 2.25 2.25Z" />
  </svg>
);

// Browser icons (simple colored SVGs)
const ChromeIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="#4285F4" />
    <circle cx="12" cy="12" r="4" fill="white" />
    <path d="M12 8h9.17a10 10 0 0 0-5.59-5.82L12 8Z" fill="#EA4335" />
    <path d="M7.42 15.5 2.83 7.68a10 10 0 0 0 2.59 10.14l4-2.32Z" fill="#FBBC05" />
    <path d="M16.58 15.5l-4.58 7.82a10 10 0 0 0 8.17-10.14h-8.17l4.58 2.32Z" fill="#34A853" />
  </svg>
);

const SafariIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="#006CFF" />
    <polygon points="12,4 14,10 12,12 10,10" fill="white" opacity="0.9" />
    <polygon points="12,20 10,14 12,12 14,14" fill="#FF3B30" opacity="0.9" />
    <circle cx="12" cy="12" r="1.5" fill="white" />
  </svg>
);

const FirefoxIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="#FF7139" />
    <circle cx="12" cy="12" r="5" fill="#3749BE" />
    <path d="M8 6c1-2 3-3 5-3 0 1-1 3-1 4s1 2 2 2c-1 1-3 1-4 0s-2-2-2-3Z" fill="#FF7139" />
  </svg>
);

const EdgeIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="#0078D7" />
    <path d="M6 14c0-4 3-7 7-7 2 0 4 1 5 3-2-1-4-1-5 0s-2 3-1 5c-3 0-6-1-6-1Z" fill="#50E6FF" />
    <circle cx="15" cy="16" r="3" fill="#00B294" />
  </svg>
);

const OperaIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="10" fill="#FF1B2D" />
    <ellipse cx="12" cy="12" rx="4" ry="7" fill="white" opacity="0.9" />
  </svg>
);

// Social/search platform icons
const GoogleIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23Z" fill="#34A853" />
    <path d="M5.84 14.09A6.68 6.68 0 0 1 5.5 12c0-.72.13-1.43.34-2.09V7.07H2.18A11.05 11.05 0 0 0 1 12c0 1.78.42 3.46 1.18 4.93l3.66-2.84Z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53Z" fill="#EA4335" />
  </svg>
);

const FacebookIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047v-2.66c0-3.025 1.792-4.696 4.533-4.696 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.93-1.956 1.886v2.282h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073Z" />
  </svg>
);

const TwitterIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

const InstagramIcon: React.FC<{ className?: string }> = ({ className = "w-4 h-4" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <defs>
      <linearGradient id="ig-grad" x1="0%" y1="100%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#FEDA75" />
        <stop offset="25%" stopColor="#FA7E1E" />
        <stop offset="50%" stopColor="#D62976" />
        <stop offset="75%" stopColor="#962FBF" />
        <stop offset="100%" stopColor="#4F5BD5" />
      </linearGradient>
    </defs>
    <rect x="2" y="2" width="20" height="20" rx="5" fill="url(#ig-grad)" />
    <circle cx="12" cy="12" r="5" stroke="white" strokeWidth="2" fill="none" />
    <circle cx="17.5" cy="6.5" r="1.5" fill="white" />
  </svg>
);

export default AnalyticsPage;
